import React from 'react'
import classNames from 'classnames'

import ProductCover from 'ui/ProductCover'
import { CustomTooltip } from '@elo-kit/components/elo-ui/custom-tooltip/CustomTooltip'
import { Ellipsis } from '@elo-kit/components/ellipsis/Ellipsis'
import { EloButton } from '@elo-kit/components/elo-button/EloButton'
import { LoadingSpinner } from '@elo-kit/components/loading-spinner/LoadingSpinner'
import CommunityTooltipMenu from 'shared/components/community/components/tooltip-menu/CommunityTooltipMenu'
import PostTime from 'shared/components/community/components/post/time/PostTime'
import ReadMoreText from 'shared/components/community/components/post/read-more-text/ReadMoreText'
import CommentForm from 'shared/components/community/comment/form/CommentForm'
import CommunityComment from 'shared/components/community/comment/CommunityComment'
import LoadMoreButton from 'shared/components/community/components/post/load-more-button/LoadMoreButton'
import Avatar from 'shared/components/community/components/avatar/Avatar'

import { COMMUNITY_NAME_MAX_LENGTH } from 'constants/communities.constants'
import { SIZES, POSITIONS, ELOPAGE_CABINETS } from '@elo-kit/constants/general.constants'

import { withStores } from 'utils/helpers.utils'
import { getFormattedCreatedTime } from 'utils/communities.utils'
import { profile } from 'utils/profileHelper.utils'

import { notify } from 'libs/common/notify'

import './community-post.scss'

const stores = ['communityPostsStore']

const ProductHeader = ({ member, createdAt, communityName, product, rejectButtonAction, approveButtonAction }) => {
  const isPayerCabinet = profile.profileType === ELOPAGE_CABINETS.payer

  return (
    <div className='post-feed-header'>
      <div className='post-feed-header__flex-container'>
        <ProductCover
          product={{
            ...product,
            ...(isPayerCabinet && { private: false, active: true }),
          }}
          showProductFormIcon
        />
        <div className='post-feed-header__container'>
          <div className='post-feed-header__post-info'>
            <div className='post-feed-header__post-name'>
              <Ellipsis maxLength={COMMUNITY_NAME_MAX_LENGTH}>{communityName}</Ellipsis>
            </div>
            <div className='post-feed-header__author-container'>
              <span className='post-feed-header__author-name'>
                {I18n.t('react.shared.community.posted_by')} {member?.username}
              </span>
              <span className='post-feed-header__posted-time'>{getFormattedCreatedTime(createdAt)}</span>
            </div>
            <div className='post-feed-header__product-name'>{product?.name}</div>
          </div>
        </div>
        {rejectButtonAction && approveButtonAction && (
          <div className='post-feed-header__buttons-container'>
            <EloButton onClick={rejectButtonAction} outlined blue>
              <i className='fas fa-times'></i>
              {I18n.t('react.shared.community.button.reject')}
            </EloButton>
            <EloButton onClick={approveButtonAction} blue>
              <i className='fas fa-check'></i>
              {I18n.t('react.shared.community.button.approve')}
            </EloButton>
          </div>
        )}
      </div>
    </div>
  )
}

const AvatarHeader = withStores([...stores, 'communityMemberStore'], (props) => {
  const {
    postId,
    labels,
    createdTime,
    editedTime,
    member,
    pinned,
    setEditPostMode,
    communityPostsStore,
    communityId,
    edited,
    isPayerCabinet,
    communityMemberStore,
    memberId: postMemberId,
    waitingForReview,
  } = props
  const { item: currentMember } = communityMemberStore
  const { username, avatar } = member || {}
  const { cropped: croppedLink, original: avatarLink } = avatar?.[0] || {}
  const { name: labelName } = labels?.[0] || {}

  const isPostOwner = currentMember?.id === postMemberId
  const avatarUrl = croppedLink || avatarLink

  const handlePinPost = async () => {
    if (!isPayerCabinet) {
      communityPostsStore.toggleLoading(true)
      communityPostsStore.resetList()
      await communityPostsStore.updateItem(postId, { communityId, pinned: !pinned })
      await communityPostsStore.fetchList({ communityId })
    }
  }

  const handleDeletePost = async () => {
    await communityPostsStore.deleteItem(postId, { communityId })
    await communityPostsStore.fetchList({ communityId }, true)
  }

  const pinMenuItem = [
    {
      show: true,
      content: pinned ? I18n.t('react.shared.button.unpin_post') : I18n.t('react.shared.button.pin_post'),
      action: handlePinPost,
    },
  ]

  const editMenuItem = [
    {
      show: true,
      content: I18n.t('react.shared.button.edit'),
      action: () => setEditPostMode(true),
    },
  ]

  const tooltipMenuItems = [
    ...(isPayerCabinet || waitingForReview ? [] : pinMenuItem),
    {
      show: true,
      content: I18n.t('react.shared.button.delete'),
      action: handleDeletePost,
    },
    ...(isPostOwner && !currentMember.muted && !waitingForReview ? editMenuItem : []),
  ]

  return (
    <>
      <div className='post-header'>
        <div className='post-header__left-container'>
          <Avatar imageUrl={avatarUrl} />
          <div className='post-header__author-container'>
            <div className='post-header__author-name'>{username}</div>
            <PostTime edited={edited} createdTime={createdTime} editedTime={editedTime} />
          </div>
        </div>
        <div className='post-header__right-container'>
          {labelName && <div className='post-header__labels'>{labelName}</div>}
          {pinned && (
            <div className='post-header--pinned-icon'>
              <CustomTooltip
                id={`unpin-${postId}`}
                placement={POSITIONS.top}
                component
                tooltipIcon={(id) => <i id={id} className='fas fa-thumbtack' onClick={handlePinPost} />}
                body={I18n.t('react.shared.button.unpin_post')}
              />
            </div>
          )}
          {(isPostOwner || !isPayerCabinet) && (
            <div className='post-header__actions'>
              <CommunityTooltipMenu id={postId} menuItems={tooltipMenuItems} placement={POSITIONS.bottom} />
            </div>
          )}
        </div>
      </div>
      <div className='post-header__mobile-left-container'>
        <Avatar imageUrl={avatarUrl} />
        <div className='post-header__author-container'>
          <div className='post-header__author-name'>{username}</div>
          <PostTime edited={edited} createdTime={createdTime} editedTime={editedTime} />
        </div>
      </div>
    </>
  )
})

const LikesAndComments = withStores([...stores, 'communityMemberStore'], (props) => {
  const {
    communityPostsStore,
    likesCount,
    commentsCount,
    liked,
    postId,
    communityId,
    communityMemberStore,
    toggleShowCreateCommentForm,
    showCreateCommentForm,
  } = props
  const { item: currentMember } = communityMemberStore

  const likeIconClasses = classNames({
    'fas fa-thumbs-up': liked,
    'far fa-thumbs-up': !liked,
  })
  const commentIconClasses = classNames({
    'far fa-comment': !showCreateCommentForm || currentMember.muted,
    'fas fa-comment': showCreateCommentForm && !currentMember.muted,
  })
  const likeLoading = communityPostsStore.likesLoadingById[postId]
  const handleSaveLike = async () => {
    const reqData = { communityId, postId }

    if (currentMember.muted && !liked) {
      return
    }

    if (liked) {
      await communityPostsStore.deleteLike(reqData)
    } else {
      const resp = await communityPostsStore.createLike(reqData, { skipErrorNotific: true })
      const { success, errorCode } = resp || {}

      if (!success && errorCode === 'muted') {
        notify('error', I18n.t('react.shared.community.notific.muted_payer'))
      }
    }
  }

  const likesCountText =
    likesCount === 1 ? I18n.t('react.shared.community.like') : I18n.t('react.shared.community.likes')

  const commentsCountText =
    commentsCount === 1 ? I18n.t('react.shared.community.comment') : I18n.t('react.shared.community.comments')

  const shownNoReactions = !commentsCount && !likesCount
  const shownLikesAndCommentsCount = !!commentsCount && !!likesCount
  return (
    <div className='likes-comments'>
      <div className='likes-comments__count'>
        {!!likesCount && <span>{`${likesCount} ${likesCountText}`}</span>}
        {shownLikesAndCommentsCount && <i className='fas fa-circle' />}
        {!!commentsCount && <span>{`${commentsCount} ${commentsCountText}`}</span>}
        {shownNoReactions && <span>{I18n.t('react.shared.community.no_reactions')}</span>}
      </div>
      <div className='likes-comments__controls-container'>
        <div className='reaction-control' onClick={handleSaveLike}>
          {likeLoading ? (
            <LoadingSpinner
              size={SIZES.extraSmall}
              loaderClassName={`${likeIconClasses} loading-spinner__loader--no-margin`}
              className='likes-comments__loading'
            />
          ) : (
            <i className={likeIconClasses} />
          )}
          <span>{liked ? I18n.t('react.shared.community.unlike') : I18n.t('react.shared.community.like')}</span>
        </div>

        <div className='reaction-control' onClick={toggleShowCreateCommentForm}>
          <i className={commentIconClasses} />
          <span>{I18n.t('react.shared.community.comment')}</span>
        </div>
      </div>
    </div>
  )
})

const Content = ({ title, imageLink, description }) => (
  <div className='community-content'>
    <div className='community-content__title'>{title}</div>
    {imageLink && <img src={imageLink} className='community-content__image' alt='community' />}
    <div className='community-content__description'>
      <ReadMoreText text={description} asHTML />
    </div>
  </div>
)

const Comments = withStores([...stores, 'communityMemberStore'], (props) => {
  const {
    comments,
    communityPostsStore,
    postId,
    communityId,
    commentsCount,
    isPayerCabinet,
    communityMemberStore,
    showCreateCommentForm,
    toggleShowCreateCommentForm,
  } = props
  const { item: currentMember } = communityMemberStore

  const commentsLength = comments?.length
  const showLoadMoreComments = commentsCount > commentsLength
  const loadMoreButtonLoading = communityPostsStore.commentsPageDataById[postId]?.loading

  const loadMoreButtonContent = loadMoreButtonLoading ? (
    <LoadingSpinner
      size={SIZES.extraSmall}
      className='loading-spinner__medium-container'
      loaderClassName='loading-spinner__loader--10-margin'
    />
  ) : (
    I18n.t('react.shared.community.button.load_more_comments')
  )

  return (
    <>
      {communityPostsStore.creatingCommentsLoadingById[postId] ? (
        <LoadingSpinner className='loading-spinner__medium-container' size={SIZES.small} />
      ) : (
        <>
          {!currentMember.muted && showCreateCommentForm && (
            <CommentForm
              {...{
                postId,
                communityId,
                toggleShowCreateCommentForm,
              }}
            />
          )}
        </>
      )}
      {!!commentsLength && (
        <>
          {comments.map((comment, index) => (
            <CommunityComment
              key={comment?.id}
              withoutBottomBorder={!showLoadMoreComments && index === comments?.length - 1}
              withoutTopBorder={!!index}
              commentsLength={commentsLength}
              {...{
                comment,
                communityId,
                isPayerCabinet,
              }}
            />
          ))}
          {showLoadMoreComments && (
            <LoadMoreButton
              button={loadMoreButtonContent}
              onClick={() => {
                if (loadMoreButtonLoading) {
                  return
                }
                communityPostsStore.handleLoadMoreComments({ communityId, postId })
              }}
              isComment
            />
          )}
        </>
      )}
    </>
  )
})

const CommunityPost = (props) => {
  const { children, onClick, waitingForReview } = props

  const communityPostClasses = classNames('community-post', {
    'community-post--cursor-pointer': onClick,
    'community-post--waiting-review': waitingForReview,
  })

  return (
    <div className={communityPostClasses} onClick={onClick}>
      {waitingForReview && (
        <div className='community-post--waiting-review header'>{I18n.t('react.shared.community.wait_for_review')}</div>
      )}
      {children}
    </div>
  )
}

CommunityPost.ProductHeader = ProductHeader
CommunityPost.Content = Content
CommunityPost.AvatarHeader = AvatarHeader
CommunityPost.LikesAndComments = LikesAndComments
CommunityPost.Comments = Comments

export default withStores(['communityPostsStore'], CommunityPost)
